import type { Mode, RankedMode } from './sim/types';

// Everything here lives only in this browser. Every access is wrapped because
// storage can be blocked (private windows, strict settings).

const PREFIX = 'rana2:';

function read<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(PREFIX + key);
    return v == null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // storage unavailable
  }
}

export interface Settings {
  music: boolean;
  sfx: boolean;
  volume: number;
  vibration: boolean;
  reducedMotion: boolean;
  highContrast: boolean;
  rhythmRing: boolean;
  /** How late the player's taps land, in ms (from calibration). */
  offsetMs: number;
}

export function defaultSettings(): Settings {
  let reduced = false;
  try {
    reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // ignore
  }
  return {
    music: true, sfx: true, volume: 0.8, vibration: true, reducedMotion: reduced,
    highContrast: false, rhythmRing: true, offsetMs: 0,
  };
}

export interface PendingScore {
  mode: RankedMode;
  runId: string;
  token: string;
  nickname: string;
  inputs: number[];
  score: number;
  savedAt: number;
}

export const store = {
  settings(): Settings {
    return { ...defaultSettings(), ...read<Partial<Settings>>('settings', {}) };
  },
  saveSettings(s: Settings): void {
    write('settings', s);
  },
  best(mode: Mode | string): number {
    return read<Record<string, number>>('best', {})[mode] ?? 0;
  },
  setBest(mode: Mode | string, score: number): boolean {
    const all = read<Record<string, number>>('best', {});
    if (score <= (all[mode] ?? 0)) return false;
    all[mode] = score;
    write('best', all);
    return true;
  },
  stars(): Record<string, number> {
    return read<Record<string, number>>('stars', {});
  },
  setStars(levelId: string, stars: number): void {
    const all = this.stars();
    if (stars > (all[levelId] ?? 0)) {
      all[levelId] = stars;
      write('stars', all);
    }
  },
  clearProgress(): void {
    write('stars', {});
    write('best', {});
  },
  nickname(): string {
    return read<string>('nickname', '');
  },
  setNickname(n: string): void {
    write('nickname', n);
  },
  /** The id of this player's best saved score per mode, to show "your position". */
  myScore(mode: RankedMode): { id: number; score: number; day: string } | null {
    return read<Record<string, { id: number; score: number; day: string }>>('mine', {})[mode] ?? null;
  },
  setMyScore(mode: RankedMode, id: number, score: number, day: string): void {
    const all = read<Record<string, { id: number; score: number; day: string }>>('mine', {});
    const cur = all[mode];
    if (!cur || score > cur.score || (mode === 'daily' && cur.day !== day)) {
      all[mode] = { id, score, day };
      write('mine', all);
    }
  },
  pending(): PendingScore[] {
    return read<PendingScore[]>('pending', []);
  },
  setPending(list: PendingScore[]): void {
    write('pending', list.slice(-10));
  },
  practice(): { period: number; scene: string } {
    return read('practice', { period: 1.3, scene: 'pond' });
  },
  setPractice(p: { period: number; scene: string }): void {
    write('practice', p);
  },
};
